import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Tooltip,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { ActiveTagStats, TagStats } from '../../types/statistics';
import { ApiService } from '../../services/api';
import { getErrorSeverity } from '../../utils/errorUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`statistics-tabpanel-${index}`}
      aria-labelledby={`statistics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const StatisticsView: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const [activeTags, setActiveTags] = useState<ActiveTagStats[]>([]);
  const [tagStats, setTagStats] = useState<TagStats[]>([]);
  const [birdsEyeView, setBirdsEyeView] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tagStatsLoading, setTagStatsLoading] = useState(true);
  const [birdsEyeLoading, setBirdsEyeLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tagStatsError, setTagStatsError] = useState<string | null>(null);
  const [birdsEyeError, setBirdsEyeError] = useState<string | null>(null);

  const fetchActiveTags = async (refresh?: boolean) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getActiveTagsStatistics(refresh);
      setActiveTags(response.active_tags);
    } catch (err) {
      console.error('Error fetching active tags:', err);
      setActiveTags([]); // Clear data on error
      setError(err instanceof Error ? err.message : 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  };

  const fetchTagStats = async (refresh?: boolean) => {
    try {
      setTagStatsLoading(true);
      setTagStatsError(null);
      const response = await ApiService.getTagStatistics(refresh);
      setTagStats(response.tag_stats);
    } catch (err) {
      console.error('Error fetching tag stats:', err);
      setTagStats([]); // Clear data on error
      setTagStatsError(err instanceof Error ? err.message : 'Failed to fetch');
    } finally {
      setTagStatsLoading(false);
    }
  };

  const fetchBirdsEyeView = async (refresh?: boolean) => {
    try {
      setBirdsEyeLoading(true);
      setBirdsEyeError(null);
      const response = await ApiService.getBirdsEyeView(refresh);
      setBirdsEyeView(response.birds_eye_view);
    } catch (err) {
      console.error('Error fetching bird\'s eye view:', err);
      setBirdsEyeView(null); // Clear data on error
      setBirdsEyeError(err instanceof Error ? err.message : 'Failed to fetch');
    } finally {
      setBirdsEyeLoading(false);
    }
  };

  const handleRefresh = () => {
    // Clear all state data before fetching fresh data
    setActiveTags([]);
    setTagStats([]);
    setBirdsEyeView(null);
    setError(null);
    setTagStatsError(null);
    setBirdsEyeError(null);
    // Fetch fresh data with cache refresh
    fetchActiveTags(true);
    fetchTagStats(true);
    fetchBirdsEyeView(true);
  };

  useEffect(() => {
    // Fetch data on mount
    fetchActiveTags();
    fetchTagStats();
    fetchBirdsEyeView();
  }, []);
  const renderActiveTagsTable = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity={getErrorSeverity(error)}>Failed to fetch</Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Active Email Statistics
        </Typography>
        <Box sx={{ overflowX: 'auto', width: '100%' }}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 1800 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 180, px: 3 }}>Stat Name</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, px: 2 }}>THC</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, px: 2 }}>CBD</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, px: 2 }}>INV</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, px: 2 }}>MED</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110, px: 2 }}>THC+CBD</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110, px: 2 }}>THC+INV</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110, px: 2 }}>CBD+INV</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110, px: 2 }}>INV+MED</TableCell>
                  <TableCell align="right" sx={{ minWidth: 140, px: 2 }}>THC+CBD+INV</TableCell>
                  <TableCell align="right" sx={{ minWidth: 140, px: 2 }}>THC+CBD+MED</TableCell>
                  <TableCell align="right" sx={{ minWidth: 180, px: 2 }}>THC+CBD+INV+MED</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, px: 2 }}>
                    <Tooltip title="Active emails with no type specified" placement="top">
                      <span>No Type</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, px: 2, fontWeight: 'bold' }}>
                    <Tooltip title="Total of all types" placement="top">
                      <span>Total</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeTags.map((stat, index) => {
                  const isTotalActiveEmails = stat.stat_name === 'Total Active Emails';
                  return (
                    <TableRow 
                      key={index}
                      sx={{
                        backgroundColor: isTotalActiveEmails ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                        fontWeight: isTotalActiveEmails ? 'bold' : 'inherit',
                        '&:hover': {
                          backgroundColor: isTotalActiveEmails ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                        }
                      }}
                    >
                      <TableCell sx={{ px: 3, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.stat_name}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.thc}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.cbd}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.inv}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.med}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.thc_cbd}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.thc_inv}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.cbd_inv}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.inv_med}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.thc_cbd_inv}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.thc_cbd_med}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.thc_cbd_inv_med}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isTotalActiveEmails ? 'bold' : 'inherit' }}>
                        {stat.empty_type}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: 'bold' }}>
                        {stat.total}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  };

  const renderTagStatsTable = () => {
    if (tagStatsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (tagStatsError) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity={getErrorSeverity(tagStatsError)}>Failed to fetch</Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Active Tag Statistics
        </Typography>
        <Box sx={{ overflowX: 'auto', width: '100%' }}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 1800 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 200, px: 3 }}>Tag</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, px: 2 }}>THC</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, px: 2 }}>CBD</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, px: 2 }}>INV</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, px: 2 }}>MED</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110, px: 2 }}>THC+CBD</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110, px: 2 }}>THC+INV</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110, px: 2 }}>CBD+INV</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110, px: 2 }}>INV+MED</TableCell>
                  <TableCell align="right" sx={{ minWidth: 140, px: 2 }}>THC+CBD+INV</TableCell>
                  <TableCell align="right" sx={{ minWidth: 140, px: 2 }}>THC+CBD+MED</TableCell>
                  <TableCell align="right" sx={{ minWidth: 180, px: 2 }}>THC+CBD+INV+MED</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, px: 2 }}>
                    <Tooltip title="Active emails with no type specified" placement="top">
                      <span>No Type</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, px: 2, fontWeight: 'bold' }}>
                    <Tooltip title="Total of all types" placement="top">
                      <span>Total</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tagStats.map((stat, index) => {
                  const isGrandTotal = stat.tag === 'GRAND TOTAL';
                  const isNoTag = stat.tag === 'No Industry Tag';
                  return (
                    <TableRow 
                      key={index}
                      sx={{
                        backgroundColor: isGrandTotal ? 'rgba(25, 118, 210, 0.08)' : isNoTag ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                        fontWeight: isGrandTotal ? 'bold' : 'inherit',
                        '&:hover': {
                          backgroundColor: isGrandTotal ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                        }
                      }}
                    >
                      <TableCell sx={{ px: 3, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.tag}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.thc}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.cbd}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.inv}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.med}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.thc_cbd}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.thc_inv}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.cbd_inv}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.inv_med}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.thc_cbd_inv}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.thc_cbd_med}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.thc_cbd_inv_med}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: isGrandTotal ? 'bold' : 'inherit' }}>
                        {stat.empty_type}
                      </TableCell>
                      <TableCell align="right" sx={{ px: 2, fontWeight: 'bold' }}>
                        {stat.total}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  };

  // Static mapping of high-level stats to individual industry tags
  const highLevelStatTags: Record<string, string[]> = {
    'THC Plant Touching': [
      'Budtender', 'Cannabis Lab', 'Distributor', 'THC Lab', 'Lab', 
      'Dispensary', 'THC Manufacturer', 'THCA Manufacturer', 'Growers', 
      'THC Cultivator', 'Extractor'
    ],
    'THC Retailers': ['Budtender', 'Delivery', 'Dispensary'],
    'THC Manufacturers': ['Pharmaceutical', 'THC Manufacturer', 'THCA Manufacturer'],
    'Cannabis Cultivators': ['Growers', 'THC Cultivator', 'Growers support'],
    'Other THC Plant Touching': ['Transporter', 'Seeds'],
    'CBD Plant Touching': [
      'Cbd Broker', 'Cbd Wholesaler', 'Hemp Lab', 'Extractor', 'Lab',
      'CBD Manufacturer', 'Cbd Pets', 'Hemp cultivator', 'Cbd Extractor',
      'Cbd Cosmetics', 'Cbd Edibles', 'Cbd Vape'
    ],
    'CBD Retailers': [
      'CBD Affiliate Network', 'CBD Franchise', 'CBD Online Store', 'CBD Shop',
      'CBD solo store', 'Cbd Store', 'CBD Wellness', 'Head Shop',
      'Online Cbd Shop', 'Wellness Center'
    ],
    'Lawyers': ['Lawyers'],
    'Hemp Cultivators': ['Hemp cultivator'],
    'Other CBD Plant Touching': ['CBD Product', 'Seeds'],
    'Journalists': ['Journalist'],
    'CBD Manufacturer': [
      'Cbd Cosmetics', 'Cbd Edibles', 'Cbd Extractor', 'CBD Manufacturer',
      'Cbd Pets', 'CBD Product', 'Cbd Vape'
    ],
    'Investors': ['Investors', 'Wealth Managers'],
    'Nurses': ['Nurses'],
    'Pharmacists': ['Pharmacists'],
    'Doctors': ['Doctors'],
    'Chiropractors': ['Chiropractor'],
    'Other Medical': ['Hospital & Healthcare'],
    'Other': [
      'Cannabis Industry', 'Cannabis Professional', 'CBD Chiropractor', 
      'CBD Professional', 'Influencer', 'Instagram consumer', 'Kaveh\'s contacts',
      'Massage Therapist', 'Post liker', 'Psychedelic', 'Recover Center',
      'Social Justice', 'Substack', 'Twitter', 'Website Subscribers', 'Writers',
      'TEST ONLY', 'Cbd Training', 'Dead', 'Not Cannabis', 'Events',
      'CBD Sport', 'CBD Doctor'
    ],
    'Ancillary': [
      'Accountants', 'Association', 'Banking', 'Cannabis Association',
      'Cannabis School', 'Cbd Association', 'Cbd Consultant', 'CBD Events',
      'CBD Industry', 'Cbd Marketing', 'Construction', 'Consultants',
      'Consumer Accessories', 'Equipment Installation', 'Extractor',
      'Fertilizer Company', 'Fertilizer Distributor', 'Growers support',
      'Hemp Bank', 'Hydroponics Store', 'Insurance', 'Lab support',
      'Cannabis Bank', 'Manufacturer Support', 'Marketing', 'Media', 'Packaging',
      'Payment processor', 'Real Estate', 'School', 'Security',
      'Software Company', 'Speakers', 'State Government', 'Hydroponic distributor',
      'THC Association', 'THC Consultant', 'CBD Media'
    ],
    'No Industry Tag': []
  };

  const renderBreakdownTable = (breakdown: any[], title: string) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Description</TableCell>
            <TableCell align="right">Number</TableCell>
            <TableCell align="right">Percent</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {breakdown.map((item, index) => {
            const tags = highLevelStatTags[item.description] || [];
            const tooltipContent = tags.length > 0 
              ? `Industry Tags: ${tags.join(', ')}`
              : 'No industry tags mapped';
            
            return (
              <TableRow key={index}>
                <TableCell>
                  <Tooltip 
                    title={tooltipContent}
                    placement="top"
                    arrow
                  >
                    <span style={{ cursor: 'help' }}>
                      {item.description}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">{item.number.toLocaleString()}</TableCell>
                <TableCell align="right">{item.percent}%</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderBirdsEyeView = () => {
    if (birdsEyeLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (birdsEyeError) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity={getErrorSeverity(birdsEyeError)}>Failed to fetch</Alert>
        </Box>
      );
    }

    if (!birdsEyeView) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error">No data available</Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Bird's Eye View
        </Typography>
        
        {/* Top row - 3 cards */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                  <Typography variant="h6">Cannabis Subscribers</Typography>
                  <Typography variant="h4">{birdsEyeView.cannabis_subscribers.toLocaleString()}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {birdsEyeView.cannabis_breakdown && birdsEyeView.cannabis_breakdown.length > 0 ? (
                  renderBreakdownTable(birdsEyeView.cannabis_breakdown, 'Cannabis Breakdown')
                ) : (
                  <Typography>No breakdown data available</Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} md={4}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                  <Typography variant="h6">Hemp Subscribers</Typography>
                  <Typography variant="h4">{birdsEyeView.hemp_subscribers.toLocaleString()}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {birdsEyeView.hemp_breakdown && birdsEyeView.hemp_breakdown.length > 0 ? (
                  renderBreakdownTable(birdsEyeView.hemp_breakdown, 'Hemp Breakdown')
                ) : (
                  <Typography>No breakdown data available</Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} md={4}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                  <Typography variant="h6">Investor Subscribers</Typography>
                  <Typography variant="h4">{birdsEyeView.investor_subscribers.toLocaleString()}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {birdsEyeView.investor_breakdown && birdsEyeView.investor_breakdown.length > 0 ? (
                  renderBreakdownTable(birdsEyeView.investor_breakdown, 'Investor Breakdown')
                ) : (
                  <Typography>No breakdown data available</Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>

        {/* Bottom row - 2 cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                  <Typography variant="h6">Medical Subscribers</Typography>
                  <Typography variant="h4">{birdsEyeView.medical_subscribers.toLocaleString()}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {birdsEyeView.medical_breakdown && birdsEyeView.medical_breakdown.length > 0 ? (
                  renderBreakdownTable(birdsEyeView.medical_breakdown, 'Medical Breakdown')
                ) : (
                  <Typography>No breakdown data available</Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} md={6}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                  <Typography variant="h6">Total Subscribers</Typography>
                  <Typography variant="h4">{birdsEyeView.total_subscribers.toLocaleString()}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexWrap: 'wrap',
        gap: 1,
        '& > *': {
          flexShrink: 0
        }
      }}>
        <Typography variant="h5" sx={{ flex: '1 1 auto', minWidth: 0 }}>
          Statistics
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading || tagStatsLoading || birdsEyeLoading}
          size="small"
          sx={{
            fontSize: '0.875rem',
            padding: '6px 12px',
            minWidth: 'auto',
            whiteSpace: 'nowrap',
            '@media (max-width: 600px)': {
              '& .MuiButton-startIcon': {
                margin: 0
              },
              '& .MuiButton-label': {
                display: 'none'
              },
              minWidth: '40px',
              padding: '6px'
            }
          }}
        >
          Refresh Statistics
        </Button>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="statistics tabs">
          <Tab label="Active Emails" />
          <Tab label="Bird's Eye View" />
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={0}>
        {renderActiveTagsTable()}
        {renderTagStatsTable()}
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        {renderBirdsEyeView()}
      </TabPanel>
    </Box>
  );
};

export default StatisticsView;
